import { useEffect, useState } from 'react';
import { useAudioServices, type MusicTrack } from '@doer/frontend-components/creativity-editor';
import { resolveAudioDuration } from '../utils/resolve-audio-duration';

export type ResolvedMusicTrack = {
  trackId: string;
  trackName: string;
  audioUrl: string;
  durationMs: number;
};

type UseAvatarsMusicTrackOptions = {
  musicTrackId: string | null;
  hasSavedPiece: boolean;
};

type UseAvatarsMusicTrackResult = {
  track: ResolvedMusicTrack | null;
  isReady: boolean;
};

/**
 * Resuelve el track de música pre-seleccionado por el user en Studio para el
 * primer boot del editor de Avatars.
 *
 * Flujo:
 *   1. Si hay `editorState` → no inyectamos.
 *   2. Si `musicTrackId` es null → user eligió "Sin música", no inyectamos.
 *   3. Carga la lista de tracks del cliente vía el `loadMusicTracks` del
 *      AudioServices context (mismo que usa el SoundPanel).
 *   4. Busca el track por id. Si no matchea → warn + skip.
 *   5. Si el backend devolvió `durationMs`, la usa. Si no, la probea con
 *      `resolveAudioDuration`.
 *
 * Cualquier fallo intermedio termina en `isReady=true, track=null` (silent skip)
 * para no bloquear el boot del editor.
 */
export const useAvatarsMusicTrack = ({
  musicTrackId,
  hasSavedPiece
}: UseAvatarsMusicTrackOptions): UseAvatarsMusicTrackResult => {
  const { loadMusicTracks } = useAudioServices();
  const [track, setTrack] = useState<ResolvedMusicTrack | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setTrack(null);
    setIsReady(false);

    if (hasSavedPiece) {
      setIsReady(true);
      return;
    }

    if (!musicTrackId) {
      setIsReady(true);
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      const tracks: MusicTrack[] | null = await loadMusicTracks();
      if (cancelled) return;

      const match = tracks?.find(t => t.id === musicTrackId);
      if (!match) {
        console.warn(`trackId="${musicTrackId}" no existe en la librería del cliente, skip.`);
        setIsReady(true);
        return;
      }

      // Si el backend ya devolvió duración, la usamos. Si no, probeamos.
      let durationMs: number | null = match.durationMs ?? null;
      if (durationMs == null) {
        try {
          durationMs = await resolveAudioDuration(match.url);
        } catch (err) {
          console.warn('probe de duración falló', err);
        }
      }

      if (cancelled) return;

      if (durationMs == null) {
        setIsReady(true);
        return;
      }

      setTrack({
        trackId: match.id,
        trackName: match.name,
        audioUrl: match.url,
        durationMs
      });
      setIsReady(true);
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [musicTrackId, hasSavedPiece, loadMusicTracks]);

  return { track, isReady };
};
